import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApplicantStatusesEnum } from '@thinknet-gmbh/platform-common';
import { Button, ButtonsWrapper, Form, Separator } from '@thinknet-gmbh/platform-common-ui';

import { useUpdateApplicantById } from 'src/hooks/applicants/update-applicant-by-id.hook';
import { VACANCY_DETAILS_TABS } from 'src/pages/vacancies/sub-pages/vacancy-details/vacancy-details.constants';

import { ICandidateDetailsProps } from './applicant-details-form.interfaces';
import { ApplicantInterviewDetails } from './components/applicant-interviews-details';
import { ApplicantFeedbacksFormList } from './components/applicant-feedbacks-form-list';
import { ApplicantInterviewFormList } from './components/applicant-interviews-form-list';
import { getCurrentApplicantStatus, checkIfFirstView } from './applicant-details-form.helpers';

const ApplicantDetailsForm = ({
  isAddedAdditionalInterview,
  setIsAddedAdditionalInterview,
  initialValues,
  applicantId,
}: ICandidateDetailsProps) => {
  const [isEditInterviews, setIsEditInterviews] = useState(false);
  const [isVisibleButtons, setIsVisibleButtons] = useState(false);
  const { t } = useTranslation(['common', 'vacancies']);
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const isFirstView = checkIfFirstView(initialValues);

  const navigateToList = () =>
    navigate(`${location.pathname}?type=${VACANCY_DETAILS_TABS.applicants}`);

  const { updateApplicant } = useUpdateApplicantById({ onSuccess: navigateToList });

  useEffect(() => {
    const interviews = form.getFieldValue('interviews');
    const additionalInterviews = form.getFieldValue('additionalInterviews');

    if (isAddedAdditionalInterview && (!interviews || !interviews.length)) {
      form.setFieldsValue({ interviews: [{}] });
    }

    if (initialValues.status !== ApplicantStatusesEnum.NEW) {
      if (isAddedAdditionalInterview && (!additionalInterviews || !additionalInterviews.length)) {
        form.setFieldsValue({ additionalInterviews: [{}] });
        setIsVisibleButtons(true);
      }
    }

    setIsAddedAdditionalInterview(false);
  }, [form, isAddedAdditionalInterview, setIsAddedAdditionalInterview, initialValues]);

  const submitForm = (values: any) => {
    const { interviews, additionalInterviews, ...rest } = values;
    const status = getCurrentApplicantStatus(initialValues, values);

    updateApplicant({
      variables: {
        applicantId,
        payload: {
          ...rest,
          status,
          interviews:
            initialValues.status === ApplicantStatusesEnum.NEW || !additionalInterviews
              ? interviews
              : [...(interviews || initialValues.interviews), ...additionalInterviews],
        },
      },
    });
  };

  const cancelEditing = () => {
    form.resetFields();
    setIsEditInterviews(false);
    setIsVisibleButtons(false);

    if (isFirstView) {
      navigateToList();
    }
  };

  return (
    <Form
      layout="vertical"
      onFinish={submitForm}
      form={form}
      initialValues={{
        ...initialValues,
        feedbacks:
          initialValues?.feedbacks.length > 0
            ? initialValues?.feedbacks
            : initialValues?.responsiblePersons.map((item: any) => ({
                feedbackFrom: item._id,
              })),

        interviews: initialValues.interviews.map((item: any) => ({
          ...item,
          date: moment(item.date),
        })),
      }}
    >
      {isFirstView ? (
        <>
          <Separator />
          <ApplicantFeedbacksFormList responsiblePersons={initialValues.responsiblePersons} />
          <ApplicantInterviewFormList
            isAddedAdditionalInterview={isAddedAdditionalInterview}
            t={t}
          />
          <ButtonsWrapper marginTop={24}>
            <Button type="primary" htmlType="submit">
              {t('common:buttons.save')}
            </Button>
            <Button onClick={cancelEditing}>{t('common:buttons.cancel')}</Button>
          </ButtonsWrapper>
        </>
      ) : (
        <div>
          {isEditInterviews ? (
            <ApplicantInterviewFormList t={t} />
          ) : (
            <ApplicantInterviewDetails
              interviews={initialValues.interviews}
              onClickEdit={() => setIsEditInterviews(true)}
            />
          )}

          <ApplicantInterviewFormList
            isAddedAdditionalInterview={isAddedAdditionalInterview}
            t={t}
            fieldName="additionalInterviews"
            hideActionButtons={() => setIsVisibleButtons(false)}
          />

          {(isEditInterviews || isVisibleButtons) && (
            <ButtonsWrapper marginTop={24}>
              <Button type="primary" htmlType="submit">
                {t('common:buttons.save')}
              </Button>
              <Button onClick={cancelEditing}>{t('common:buttons.cancel')}</Button>
            </ButtonsWrapper>
          )}
        </div>
      )}
    </Form>
  );
};

export default ApplicantDetailsForm;
